import React from "react";
// eslint-disable-next-line
import { MouseEvent, useEffect, useMemo, useState } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Loader from "../../../../../components/Loader";
import FormCompanyCommentAdd from "./FormCompanyCommentAdd";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableTable from "../../../../../components/ReusableComponent/ReusableTable";
import { openSnackbar } from "../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";
import ExpandingDetail from "./ExpandingDetail";
import { CompanyComment } from "../../../../../types/site-params/commentAction";

// ==============================|| COMPANY COMMENTS LIST ||============================== //

const CompanyComments = ({
  parentNumber,
  companyId,
}: {
  parentNumber?: number;
  companyId?: string;
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CompanyComment | null>(null);
  const [selected, setSelected] = useState<any>("");
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );

  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<CompanyComment>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle1">{row.original.id}</Typography>
        ),
      },

      {
        header: () => <FormattedMessage id="action" />,
        accessorKey: "action",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.action?.value_en}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="company" />,
        accessorKey: "company",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.company?.name}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="from user" />,
        accessorKey: "from_user",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.from_user?.name}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="to user" />,
        accessorKey: "to_user",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.to_user?.name}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="date" />,
        accessorKey: "date",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.date}</Typography>
        ),
      },
      // {
      //   header: () => <FormattedMessage id="text" />,
      //   accessorKey: "text",
      //   meta: { className: "cell-center" },
      //   cell: ({ row }) => (
      //     <Typography variant="subtitle2">{row.original.text}</Typography>
      //   ),
      // },
      {
        header: () => <FormattedMessage id="created_at" />,
        accessorKey: "created_at",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.created_at}</Typography>
        ),
      },

      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: { className: "cell-center" },
        disableSortBy: true,
        cell: ({ row }) => {
          const isExpanded = String(expandedRowId) === String(row.original.id);
          const collapseIcon = isExpanded ? (
            <CloseOutlined
              style={{
                color: theme.palette.error.main,
              }}
            />
          ) : (
            <EyeOutlined />
          );
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title={isExpanded ? "close details" : "view details"}>
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    setExpandedRowId(
                      isExpanded ? null : String(row.original.id)
                    );
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setSelected(row.original);
                    setSelectedCustomer(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CompanyComment[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/company_comments/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Company comment deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete company comment",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(
        `${
          companyId
            ? `/company_comments/comment/${companyId}`
            : "/company_comments"
        }?page=${currentPage}${searchText ? `&title=${searchText}` : ""}`
      );
      const { data, meta } = response.data;

      setResult(data);
      setCountPage(meta);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching company comments",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText, companyId]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleSearch = () => {
    setloadingSearsh(true);
    setCurrenttPage(1);
    setSearchText(tempSearchText);
    setloadingSearsh(false);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <ReusableTable
          data={result}
          columns={columns}
          countPage={countPage}
          handlePageChange={handlePageChange}
          handleSearchChange={handleSearch}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setCurrenttPage={setCurrenttPage}
          loadingSearsh={loadingSearsh}
          searchedtext={tempSearchText}
          setSearchedtext={setTempSearchText}
          modalToggler={() => {
            setCustomerModal(true);
            setSelectedCustomer(null);
          }}
          renderDetailPanel={({ row }: any) => (
            <ExpandingDetail data={row.original} />
          )}
          expandedRowId={expandedRowId}
          setExpandedRowId={setExpandedRowId}
        />
      )}
      <ReusableDeleteDialog
        title={selected?.name}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <FormCompanyCommentAdd
        open={customerModal}
        setResult={setResult}
        loading={formLoading}
        setLoading={setFormLoading}
        fetchData={result}
        id={selected?.company_id}
        companyId={companyId}
        companyComment={selectedCustomer}
        handleClose={() => setCustomerModal(false)}
      />
    </>
  );
};

export default CompanyComments;
